import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { DeliveryCompletedSummaryFilters } from '../../components/DeliveryCompletedSummaryFilter';
import {
  GET_DSP_DELIVERY_COMPLETED_SUMMARY_PAGE_LIST,
  GetDspDeliveryCompletedSummaryPageListResponse,
  DspDeliveryCompletedSummaryPageRequestType,
  DspDeliveryCompletedSummaryType,
  GET_DSP_DELIVERY_COMPLETED_SUMMARY_BY_DRIVER_GROUP_PAGE_LIST,
  GetDspDeliveryCompletedSummaryByDriverGroupPageListResponse,
  DspDeliveryCompletedSummaryByDriverGroupPageRequestType,
  DspDeliveryCompletedSummaryByDriverGroupType
} from '../queries/getDspDeliveryCompletedSummaryList';

export const useDspDeliveryCompletedSummaryQuery = () => {
  const defaultDateRange: [dayjs.Dayjs, dayjs.Dayjs] = [
    dayjs().subtract(7, 'day'),
    dayjs()
  ];

  const initialPageRequest: DspDeliveryCompletedSummaryPageRequestType = {
    filter: {
      deliveredDateStart: defaultDateRange[0].format('YYYY-MM-DD'),
      deliveredDateEnd: defaultDateRange[1].format('YYYY-MM-DD')
    },
    pageRequest: {
      page: 1,
      size: 10,
      sortBy: 'deliveredCount',
      sortDirection: 'DESC'
    }
  };

  const [pageRequest, setPageRequest] = useState<DspDeliveryCompletedSummaryPageRequestType>(initialPageRequest);

  const { data, loading, error } = useQuery<GetDspDeliveryCompletedSummaryPageListResponse>(
    GET_DSP_DELIVERY_COMPLETED_SUMMARY_PAGE_LIST,
    {
      variables: {
        pageRequest
      },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true
    }
  );

  const handleFilter = (filters: DeliveryCompletedSummaryFilters, page: number, size: number) => {
    const range = filters.dateRange ?? defaultDateRange;
    setPageRequest({
      filter: {
        deliveredDateStart: range[0].format('YYYY-MM-DD'),
        deliveredDateEnd: range[1].format('YYYY-MM-DD')
      },
      pageRequest: {
        page,
        size,
        sortBy: 'deliveredCount',
        sortDirection: 'DESC'
      }
    });
  };

  const records: DspDeliveryCompletedSummaryType[] = useMemo(() => {
    return data?.getDspDeliveryCompletedSummaryPageList?.pageResponse?.content || [];
  }, [data]);

  const pagination = useMemo(() => {
    const pageResponse = data?.getDspDeliveryCompletedSummaryPageList?.pageResponse;
    return {
      page: pageResponse?.page ?? 1,
      size: pageResponse?.size ?? 10,
      totalElements: pageResponse?.totalElements ?? 0
    };
  }, [data]);

  return {
    records,
    loading,
    error,
    handleFilter,
    pagination
  };
};

export const useDspDeliveryCompletedSummaryByDriverGroupQuery = () => {
  const defaultDateRange: [dayjs.Dayjs, dayjs.Dayjs] = [
    dayjs().subtract(7, 'day'),
    dayjs()
  ];

  const initialPageRequest: DspDeliveryCompletedSummaryByDriverGroupPageRequestType = {
    filter: {
      deliveredDateStart: defaultDateRange[0].format('YYYY-MM-DD'),
      deliveredDateEnd: defaultDateRange[1].format('YYYY-MM-DD')
    },
    pageRequest: {
      page: 1,
      size: 10
    }
  };

  const [pageRequest, setPageRequest] = useState<DspDeliveryCompletedSummaryByDriverGroupPageRequestType>(initialPageRequest);

  const { data, loading, error } = useQuery<GetDspDeliveryCompletedSummaryByDriverGroupPageListResponse>(
    GET_DSP_DELIVERY_COMPLETED_SUMMARY_BY_DRIVER_GROUP_PAGE_LIST,
    {
      variables: {
        pageRequest
      },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true
    }
  );

  const groups: DspDeliveryCompletedSummaryByDriverGroupType[] = useMemo(() => {
    return data?.getDspDeliveryCompletedSummaryByDriverGroupPageList?.pageResponse?.content || [];
  }, [data]);

  const records: DspDeliveryCompletedSummaryType[] = useMemo(() => {
    return groups.flatMap((g) => g.items || []);
  }, [groups]);

  const pagination = useMemo(() => {
    const pageResponse = data?.getDspDeliveryCompletedSummaryByDriverGroupPageList?.pageResponse;
    return {
      page: pageResponse?.page ?? 1,
      size: pageResponse?.size ?? 10,
      totalElements: pageResponse?.totalElements ?? 0
    };
  }, [data]);

  const handleFilter = (filters: DeliveryCompletedSummaryFilters, page: number, size: number) => {
    const range = filters.dateRange ?? defaultDateRange;
    setPageRequest({
      filter: {
        deliveredDateStart: range[0].format('YYYY-MM-DD'),
        deliveredDateEnd: range[1].format('YYYY-MM-DD')
      },
      pageRequest: {
        page,
        size
      }
    });
  };

  return {
    groups,
    records,
    loading,
    error,
    handleFilter,
    pagination
  };
};
